/**
 * Game Summary — FR-SUMMARY-01…06, AC-GSUMM-001…003.
 *
 * The high-level box score every coach on the staff reads between series: line
 * score by quarter with the win/loss result, clock / quarter / possession,
 * drive-by-drive scoring summary and the full Offense / Defense / Special Teams
 * stat sheets. One call to `GET /stats/v1/summary/{event_id}?unit=all` feeds all
 * of it; a live subscription refetches when a play lands, so entry → summary
 * propagation needs no refresh.
 *
 * Layout follows `design/responsive`: a vertical accordion on a phone (score
 * widget stays pinned above it) and a two-column side-by-side sheet from tablet
 * landscape up — General/Drives left, the three stat blocks right.
 *
 * RBAC: `fanatic` gets the team-aggregate projection (`StatBlock isSummaryOnly`)
 * — score, drives and totals, no per-attempt detail. Everyone else gets the
 * full sheet. The server applies the same boundary; this is defence in depth,
 * not the only gate.
 *
 * Video: the slot renders `<VideoEmbed>`, which asks the video service for
 * whatever provider the tenant configured — this page never knows whether it is
 * showing Hudl or YouTube. For a Fanatic on a coaches-only provider the service
 * answers 403 and the slot collapses entirely (`isHiddenWhenUnavailable`), which
 * is AC-VIDEO-003's "hidden, not shown as unavailable" for the fan view.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useActiveEvent } from '../../app/active-event-context';
import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import { UNIT_TITLES } from '../../components/odst-segmented-toggle';
import { PageHeader } from '../../components/page-header';
import { QuickLinksBar } from '../../components/quick-links-bar';
import { StatusPill } from '../../components/status-pill';
import { VideoEmbed } from '../../components/video-embed';
import { APP_VIEWS } from '../../lib/constants';
import type { HealthSocketFactory, PlayArchiveEvent } from '../../lib/live-client';
import { usePlayArchiveSubscription } from '../../lib/live-client';
import { useIsPhoneViewport } from '../archive/use-archive';
import { TableExportButton } from '../integrations/export-dialog';

import { ScoreByQuarter, ScoringSummary, type DriveFilters } from './scoring-summary';
import { StatBlock } from './stat-block';
import { buildGameSummaryExportSource } from './summary-export';
import {
  readDriveFilters,
  readSections,
  SUMMARY_SECTIONS,
  SUMMARY_STAT_UNITS,
  teamNameFromSlug,
  useSummaryFeed,
  type SummarySectionId,
} from './use-summary';

const SECTION_TITLES: Record<SummarySectionId, string> = {
  drives: 'Scoring Summary',
  offense: UNIT_TITLES.offense,
  defense: UNIT_TITLES.defense,
  special_teams: UNIT_TITLES.special_teams,
};

interface AccordionSectionProps {
  id: SummarySectionId;
  isOpen: boolean;
  onToggle: (id: SummarySectionId) => void;
  children: JSX.Element;
}

function AccordionSection({ id, isOpen, onToggle, children }: AccordionSectionProps): JSX.Element {
  return (
    <div className="summary-accordion__item" data-open={isOpen} data-section={id}>
      <h2 className="summary-accordion__heading">
        <button
          type="button"
          className="summary-accordion__trigger"
          aria-expanded={isOpen}
          aria-controls={`summary-section-${id}`}
          onClick={() => onToggle(id)}
          data-testid={`summary-accordion-${id}`}
        >
          <span>{SECTION_TITLES[id]}</span>
          <span className="summary-accordion__chevron" aria-hidden="true">
            {isOpen ? '▾' : '▸'}
          </span>
        </button>
      </h2>
      <div
        id={`summary-section-${id}`}
        className="summary-accordion__panel"
        hidden={!isOpen}
        role="region"
        aria-label={SECTION_TITLES[id]}
      >
        {children}
      </div>
    </div>
  );
}

export interface GameSummaryPageProps {
  /** Injected by tests to drive the live channel deterministically. */
  socketFactory?: HealthSocketFactory;
}

export function GameSummaryPage({ socketFactory }: GameSummaryPageProps = {}): JSX.Element {
  const apiClient = useApiClient();
  const { persona, tenantSlug } = useSession();
  const { activeEvent, activeEventId } = useActiveEvent();
  const isPhone = useIsPhoneViewport();
  const [searchParams, setSearchParams] = useSearchParams();

  const eventId = searchParams.get('event_id') ?? activeEventId;
  const { feed, isLoading, error, reload } = useSummaryFeed(apiClient, eventId);

  const isFanatic = persona === 'fanatic';
  const teamName = teamNameFromSlug(tenantSlug);
  const openSections = useMemo(() => readSections(searchParams.get('open')), [searchParams]);
  const driveFilters = useMemo(
    () => readDriveFilters(searchParams.get('poss'), searchParams.get('drives')),
    [searchParams]
  );

  const patchParams = useCallback(
    (patch: Record<string, string | null>): void => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(patch)) {
        if (value === null) next.delete(key);
        else next.set(key, value);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const toggleSection = useCallback(
    (id: SummarySectionId): void => {
      const next = new Set(openSections);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      patchParams({ open: [...next].join(',') });
    },
    [openSections, patchParams]
  );

  const setDriveFilters = useCallback(
    (filters: DriveFilters): void => {
      patchParams({
        poss: filters.scope === 'all' ? null : filters.scope,
        drives: filters.isScoringOnly ? 'scoring' : null,
      });
    },
    [patchParams]
  );

  // Aggregates are recomputed server-side on every active-version change; the
  // client only needs to know that something landed and re-read them.
  const onLiveEvent = useCallback(
    (message: PlayArchiveEvent): void => {
      if (message.payload.event_id !== eventId) return;
      reload();
    },
    [eventId, reload]
  );
  const live = usePlayArchiveSubscription({
    eventId,
    onEvent: onLiveEvent,
    // Free tier (FR-LIVE-02): no socket — re-read the aggregates every 5 s.
    onPoll: reload,
    socketFactory,
  });

  if (eventId === null) {
    return (
      <section className="view game-summary">
        <PageHeader title="Game Summary" />
        <div className="banner banner--info">
          <p className="banner__title">No event selected</p>
          <p>Pick a game or practice from the event switcher above to see its summary.</p>
        </div>
        <QuickLinksBar currentView={APP_VIEWS.gameSummary} />
      </section>
    );
  }

  const general = feed?.statistics.general ?? null;
  const drives = feed?.statistics.scoring_summary ?? [];
  const opponentName = general?.opponent_name ?? activeEvent?.opponent_name ?? 'Opponent';

  const statBlocks = SUMMARY_STAT_UNITS.map((unit) => (
    <StatBlock
      key={unit}
      unit={unit}
      stats={feed?.statistics[unit]}
      isSummaryOnly={isFanatic}
      isLoading={isLoading}
      headingLevel={isPhone ? 3 : 2}
    />
  ));

  const driveSummary = (
    <ScoringSummary
      drives={drives}
      teamName={teamName}
      opponentName={opponentName}
      filters={driveFilters}
      onFiltersChange={setDriveFilters}
      isLoading={isLoading}
    />
  );

  const liveLabel =
    live.status === 'live' ? 'Live' : live.status === 'polling' ? 'Auto-refresh · 5s' : live.status;
  const liveTone =
    live.status === 'live' ? 'success' : live.status === 'offline' ? 'danger' : 'neutral';

  const video =
    activeEventId === null ? null : (
      <section className="game-summary__video" aria-label="Game video">
        <VideoEmbed
          eventId={activeEventId}
          title={`${activeEvent?.name ?? 'Game'} video`}
          className="video-embed--sidebar"
          isHiddenWhenUnavailable={isFanatic}
        />
      </section>
    );

  return (
    <section
      className="view game-summary"
      data-layout={isPhone ? 'accordion' : 'columns'}
      data-refreshing={isLoading && feed !== null}
      data-testid="game-summary"
    >
      <PageHeader
        className="game-summary__header"
        title="Game Summary"
        context={
          <>
            <span className="game-summary__subtitle">
              {activeEvent?.name ?? 'Selected event'} · {teamName} vs {opponentName}
            </span>
            <span className="game-summary__stamp">
              {feed === null
                ? 'Waiting for the first snapshot…'
                : `Updated ${new Date(feed.computed_at).toLocaleTimeString()} · ${feed.play_count} plays counted`}
            </span>
          </>
        }
        meta={
          <span className="game-summary__live" data-live={live.status}>
            <StatusPill tone={liveTone}>{liveLabel}</StatusPill>
          </span>
        }
        actions={
          isFanatic || feed === null ? null : (
            <TableExportButton
              source={() =>
                buildGameSummaryExportSource({
                  feed,
                  event: activeEvent?.event_id === eventId ? activeEvent : null,
                  teamName,
                  opponentName,
                  driveFilters,
                  isSummaryOnly: isFanatic,
                })
              }
            />
          )
        }
      />

      {activeEvent?.status === 'closed' ? (
        <p className="banner banner--info game-summary__notice" role="status">
          {activeEvent.name} is closed. These are the final numbers — nothing more will arrive on
          the live feed.
        </p>
      ) : null}

      {feed !== null && feed.unresolved_conflicts > 0 ? (
        <p
          className="banner banner--warning game-summary__notice"
          role="status"
          data-testid="summary-conflict-banner"
        >
          {feed.unresolved_conflicts} play
          {feed.unresolved_conflicts === 1 ? '' : 's'} still ha
          {feed.unresolved_conflicts === 1 ? 's' : 've'} competing versions. These totals count the
          active version only — resolve them in the Play Archive to confirm.
        </p>
      ) : null}

      {error !== null ? (
        <div className="banner banner--error" role="alert">
          <p className="banner__title">Summary unavailable</p>
          <p>{error}</p>
          <button type="button" className="btn btn--secondary" onClick={reload}>
            Try again
          </button>
        </div>
      ) : null}

      {general === null ? (
        <>
          <p className="game-summary__placeholder" role="status">
            {isLoading ? 'Loading the game summary…' : 'No summary yet for this event.'}
          </p>
          {video}
        </>
      ) : (
        <>
          <ScoreByQuarter general={general} teamName={teamName} />

          {isPhone ? (
            <>
              <div className="summary-accordion" data-testid="summary-accordion">
                {SUMMARY_SECTIONS.map((section) => (
                  <AccordionSection
                    key={section}
                    id={section}
                    isOpen={openSections.has(section)}
                    onToggle={toggleSection}
                  >
                    {section === 'drives'
                      ? driveSummary
                      : statBlocks[SUMMARY_STAT_UNITS.indexOf(section)]}
                  </AccordionSection>
                ))}
              </div>
              {video}
            </>
          ) : (
            <div className="game-summary__columns" data-testid="summary-columns">
              <div className="game-summary__column game-summary__column--general">
                {driveSummary}
                {video}
              </div>
              <div className="game-summary__column game-summary__column--stats">{statBlocks}</div>
            </div>
          )}
        </>
      )}

      <QuickLinksBar currentView={APP_VIEWS.gameSummary} />
    </section>
  );
}
